import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from "react";
import api from "../api/axios";

const AuthContext = createContext();

// 7 days in milliseconds - must match backend JWT expiresIn: "7d"
const TOKEN_EXPIRY_TIME = 7 * 24 * 60 * 60 * 1000;

export const AuthProvider = ({ children }) => {
  const [token, setToken] = useState(() => {
    const storedToken = localStorage.getItem("locket_jwt");
    const storedExpiry = localStorage.getItem("locket_jwt_expiry");

    if (storedToken && storedExpiry) {
      const expiryTime = parseInt(storedExpiry, 10);
      const now = Date.now();
      if (now < expiryTime) {
        return storedToken;
      } else {
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
        return null;
      }
    }
    return null;
  });

  const [user, setUser] = useState(null);
  const expiryTimerRef = useRef(null);

  const isAuthenticated = !!token;

  // Set up expiry timer when token changes
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }

    if (token) {
      // Use the stored expiry (set by login()) rather than overriding it with a fresh 7 days.
      // This prevents token lifetime from silently extending on every app reload.
      const storedExpiry = parseInt(localStorage.getItem("locket_jwt_expiry") || "0", 10);
      const expiryTime = storedExpiry > Date.now() ? storedExpiry : Date.now() + TOKEN_EXPIRY_TIME;

      localStorage.setItem("locket_jwt", token);
      localStorage.setItem("locket_jwt_expiry", expiryTime.toString());

      const msLeft = expiryTime - Date.now();
      expiryTimerRef.current = setTimeout(() => {
        setToken(null);
        setUser(null);
        localStorage.removeItem("locket_jwt");
        localStorage.removeItem("locket_jwt_expiry");
      }, Math.max(0, msLeft));
    } else {
      localStorage.removeItem("locket_jwt");
      localStorage.removeItem("locket_jwt_expiry");
      setUser(null);
    }

    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
      }
    };
  }, [token]);

  const fetchUser = useCallback(async () => {
    if (!token) return null;
    try {
      const res = await api.get("/qr/me", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setUser(res.data);
      return res.data;
    } catch {
      return null;
    }
  }, [token]);

  const login = (jwt) => {
    const expiryTime = Date.now() + TOKEN_EXPIRY_TIME;
    localStorage.setItem("locket_jwt", jwt);
    localStorage.setItem("locket_jwt_expiry", expiryTime.toString());
    setToken(jwt);
  };

  const register = async ({ name, phone, email, password }) => {
    const res = await api.post("/qr/register", { name, phone, email, password });
    if (res.data?.token) {
      login(res.data.token);
    }
    return res.data;
  };

  const logout = useCallback(() => {
    // Best-effort server-side token revocation (fire-and-forget)
    if (token) {
      api.post("/qr/logout").catch(() => {});
    }
    setToken(null);
    setUser(null);
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
  }, [token]);

  return (
    <AuthContext.Provider value={{ token, user, isAuthenticated, login, logout, register, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
